/**
 * The shared state behind the side-navigation badges and the Approvals nav item: whether the
 * Approvals workspace exists for the signed-in user, how many days wait for their approval, and
 * how many of their own days are still to submit.
 *
 * It is module-level reactive state (the pattern of `system-messages/system-messages-data.js`)
 * rather than per-component, because the navigation lives in PrimaryLayout, which is mounted once
 * for the whole signed-in session, while the things that change the counts (approve, reject,
 * submit, recall, resubmit) happen on other pages. Those pages call
 * `refreshNavCountsAsync({ force: true })` after their action and the badge follows.
 *
 * Every call here is a background read with no overlay and never throws. A read that fails keeps
 * the last known value (an Approvals count that can't be refreshed shouldn't vanish).
 *
 * Refusals are avoided rather than handled: the approver count is only asked for when
 * availability says the user is an approver, so a plain timekeeper never triggers a
 * not-authorised toast.
 */

import { reactive } from "vue";
import globalStore from "/globalStore.js";
import
{
	getApprovalsAvailabilityAsync, getApprovalCountsAsync, getMyOutstandingTimesheetCountAsync,
} from "./approvals-api.js";


/** Counts are re-read at most this often unless a caller forces it. */
export const NAV_COUNTS_THROTTLE_MS = 60 * 1000;

/** Availability (is approval on, is this an approver) changes rarely; re-read it this often. */
const AVAILABILITY_STALE_MS = 5 * 60 * 1000;

/**
 * How many weeks the Approvals window covers until the server has said (it is the System configuration key
 * `Timesheets.ApprovalsWindowWeeks`, which defaults to this).
 */
const DEFAULT_WINDOW_WEEKS = 5;




//-- State

/**
 * `Availability` is null until it has loaded, so the nav item stays hidden rather than flashing.
 * Otherwise `{ ApprovalsEnabled, IsApprover, ScopeKind, HasMappedTeam, WindowWeeks }`.
 */
export const approvalsNavState = reactive({
	Availability: null,
	PendingCount: 0,
	MyOutstandingCount: 0,
	LastLoadedAt: 0,
});


let availabilityLoadedAt = 0;
let availabilityInFlight = null;
let countsInFlight = null;




//-- Availability

/**
 * Load whether the Approvals workspace exists for this user. Cached; re-read when stale or forced.
 *
 * @returns {Promise<object|null>} The availability, or null if it has never loaded.
 */
export async function loadApprovalsAvailabilityAsync(options = {})
{
	const isFresh = approvalsNavState.Availability !== null && (Date.now() - availabilityLoadedAt) < AVAILABILITY_STALE_MS;

	if (isFresh && options.force !== true) return approvalsNavState.Availability;
	if (availabilityInFlight) return await availabilityInFlight;

	availabilityInFlight = (async () =>
	{
		const responseData = await getApprovalsAvailabilityAsync();
		if (responseData == null) return approvalsNavState.Availability;

		approvalsNavState.Availability = {
			ApprovalsEnabled: responseData.ApprovalsEnabled === true,
			IsApprover: responseData.IsApprover === true,
			ScopeKind: responseData.ScopeKind || "None",
			HasMappedTeam: responseData.HasMappedTeam === true,
			WindowWeeks: responseData.WindowWeeks > 0 ? responseData.WindowWeeks : DEFAULT_WINDOW_WEEKS,
		};
		availabilityLoadedAt = Date.now();

		return approvalsNavState.Availability;
	})();

	try
	{
		return await availabilityInFlight;
	}
	finally
	{
		availabilityInFlight = null;
	}
}


/**
 * Is the Approvals workspace available to this user: approval switched on for the account and
 * the user an approver. False until availability has loaded.
 */
export function isApprovalsAvailable()
{
	const availability = approvalsNavState.Availability;

	return availability !== null && availability.ApprovalsEnabled && availability.IsApprover;
}




/**
 * How many weeks the Approvals window covers (the current week and the weeks before it), for wording the screens and tooltips.
 * The default until availability has loaded.
 */
export function getApprovalsWindowWeeks()
{
	const availability = approvalsNavState.Availability;

	return availability !== null ? availability.WindowWeeks : DEFAULT_WINDOW_WEEKS;
}




/**
 * Where the signed-in user stands with the workspace, as a code for `approvalsAccessStates`:
 * `loading` (availability not read yet), `off` (approval isn't switched on for the account),
 * `notApprover`, `noTeam` (a Team-scope approver with no team mapped to them, D-03) or `ok`.
 * The screens call the read endpoints only on `ok`.
 */
export function getApprovalsAccessState()
{
	const availability = approvalsNavState.Availability;

	if (availability === null) return "loading";
	if (!availability.ApprovalsEnabled) return "off";
	if (!availability.IsApprover) return "notApprover";
	if (availability.ScopeKind === "Team" && !availability.HasMappedTeam) return "noTeam";

	return "ok";
}




//-- Counts

/**
 * Refresh the nav counts. Throttled to once a minute so it can be called on every route change;
 * pass `{ force: true }` after an action that changes them. Concurrent calls share one request.
 */
export async function refreshNavCountsAsync(options = {})
{
	if (options.force !== true && (Date.now() - approvalsNavState.LastLoadedAt) < NAV_COUNTS_THROTTLE_MS) return;
	if (countsInFlight) return await countsInFlight;

	countsInFlight = (async () =>
	{
		// Stamp up front so a burst of route changes during the request doesn't queue more.
		approvalsNavState.LastLoadedAt = Date.now();

		await loadApprovalsAvailabilityAsync();

		const [pending, mine] = await Promise.all([
			isApprovalsAvailable() ? getApprovalCountsAsync() : Promise.resolve(null),
			canUseTimesheets() ? getMyOutstandingTimesheetCountAsync() : Promise.resolve(null),
		]);

		if (pending != null) approvalsNavState.PendingCount = pending.PendingCount || 0;
		if (!isApprovalsAvailable()) approvalsNavState.PendingCount = 0;
		if (mine != null) approvalsNavState.MyOutstandingCount = mine.Count || 0;
	})();

	try
	{
		await countsInFlight;
	}
	finally
	{
		countsInFlight = null;
	}
}


/**
 * The count behind a navigation item's `badgeKey` (see `navBadges` in approvals-display-values.js).
 * An unknown key counts as 0, which hides the badge.
 */
export function getNavBadgeCount(badgeKey)
{
	if (badgeKey === "approvalsPending") return isApprovalsAvailable() ? approvalsNavState.PendingCount : 0;
	if (badgeKey === "myOutstanding") return approvalsNavState.MyOutstandingCount;

	return 0;
}


/**
 * Whether a navigation item with a `visibleWhen` key should show. An item without one always
 * shows; an unknown key hides it.
 */
export function isNavKeyVisible(visibleWhen)
{
	if (!visibleWhen) return true;
	if (visibleWhen === "approvalsAvailable") return isApprovalsAvailable();

	return false;
}




//-- Session

/**
 * Forget everything: on sign-out, so the next person to sign in on this browser doesn't see the
 * previous person's counts or Approvals item before the first read lands.
 */
export function clearApprovalsNavCache()
{
	approvalsNavState.Availability = null;
	approvalsNavState.PendingCount = 0;
	approvalsNavState.MyOutstandingCount = 0;
	approvalsNavState.LastLoadedAt = 0;
	availabilityLoadedAt = 0;
}




//-- Methods

// "My outstanding" is the Timesheets module's own count; only ask for it where that module exists.
function canUseTimesheets()
{
	const user = globalStore.currentUser;
	if (!user) return false;

	return user.hasFeatureAccess("Timesheets.Enabled") && user.hasFeatureAccess("Timesheets.Core");
}
